// Copyright (c) Microsoft Corporation. All rights reserved.
// Licensed under the MIT License.
import { StorageType } from "./Enums";
import { CoreUtils, EventHelper, _InternalMessageId, LoggingSeverity, getGlobal, getGlobalInst, getWindow, getDocument, getNavigator, getPerformance, getLocation, hasJSON, getJSON, strPrototype, objForEachKey } from "@microsoft/applicationinsights-core-js";
import { RequestHeaders } from "./RequestResponseHeaders";
import { DataSanitizer } from "./Telemetry/Common/DataSanitizer";
var _navigator = getNavigator();
var _isString = CoreUtils.isString;
var _uaDisallowsSameSiteNone = null;
function _endsWith(value, search) {
    var len = value.length;
    var start = len - search.length;
    return value.substring(start >= 0 ? start : 0, len) === search;
}
var Util = /** @class */ (function () {
    function Util() {
    }
    Util.createDomEvent = function (eventName) {
        var event = null;
        if (CoreUtils.isFunction(Event)) {
            event = new Event(eventName);
        }
        else {
            var doc = getDocument();
            if (doc && doc.createEvent) {
                event = doc.createEvent("Event");
                event.initEvent(eventName, true, true);
            }
        }
        return event;
    };
    /*
     * Force the SDK not to use local and session storage
    */
    Util.disableStorage = function () {
        Util._canUseLocalStorage = false;
        Util._canUseSessionStorage = false;
    };
    /**
     * Gets the localStorage object if available
     * @return {Storage} - Returns the storage object if available else returns null
     */
    Util._getLocalStorageObject = function () {
        if (Util.canUseLocalStorage()) {
            return Util._getVerifiedStorageObject(StorageType.LocalStorage);
        }
        return null;
    };
    /**
     * Tests storage object (localStorage or sessionStorage) to verify that it is usable
     * More details here: https://mathiasbynens.be/notes/localstorage-pattern
     * @param storageType Type of storage
     * @return {Storage} Returns storage object verified that it is usable
     */
    Util._getVerifiedStorageObject = function (storageType) {
        var storage = null;
        var fail;
        var uid;
        try {
            if (CoreUtils.isNullOrUndefined(getGlobal())) {
                return null;
            }
            uid = new Date;
            storage = storageType === StorageType.LocalStorage ? getGlobalInst("localStorage") : getGlobalInst("sessionStorage");
            storage.setItem(uid.toString(), uid.toString());
            fail = storage.getItem(uid.toString()) !== uid.toString();
            storage.removeItem(uid.toString());
            if (fail) {
                storage = null;
            }
        }
        catch (exception) {
            storage = null;
        }
        return storage;
    };
    /**
     *  Checks if endpoint URL is application insights internal injestion service URL.
     *
     *  @param endpointUrl Endpoint URL to check.
     *  @returns {boolean} True if if endpoint URL is application insights internal injestion service URL.
     */
    Util.isInternalApplicationInsightsEndpoint = function (endpointUrl) {
        return Util._internalEndpoints.indexOf(endpointUrl.toLowerCase()) !== -1;
    };
    /**
     *  Check if the browser supports local storage.
     *
     *  @returns {boolean} True if local storage is supported.
     */
    Util.canUseLocalStorage = function () {
        if (Util._canUseLocalStorage === undefined) {
            Util._canUseLocalStorage = !!Util._getVerifiedStorageObject(StorageType.LocalStorage);
        }
        return Util._canUseLocalStorage;
    };
    /**
     *  Get an object from the browser's local storage
     *
     *  @param {string} name - the name of the object to get from storage
     *  @returns {string} The contents of the storage object with the given name. Null if storage is not supported.
     */
    Util.getStorage = function (logger, name) {
        var storage = Util._getLocalStorageObject();
        if (storage !== null) {
            try {
                return storage.getItem(name);
            }
            catch (e) {
                Util._canUseLocalStorage = false;
                logger.throwInternal(LoggingSeverity.WARNING, _InternalMessageId.BrowserCannotReadLocalStorage, "Browser failed read of local storage. " + Util.getExceptionName(e), { exception: Util.dump(e) });
            }
        }
        return null;
    };
    /**
     *  Set the contents of an object in the browser's local storage
     *
     *  @param {string} name - the name of the object to set in storage
     *  @param {string} data - the contents of the object to set in storage
     *  @returns {boolean} True if the storage object could be written.
     */
    Util.setStorage = function (logger, name, data) {
        var storage = Util._getLocalStorageObject();
        if (storage !== null) {
            try {
                storage.setItem(name, data);
                return true;
            }
            catch (e) {
                Util._canUseLocalStorage = false;
                logger.throwInternal(LoggingSeverity.WARNING, _InternalMessageId.BrowserCannotWriteLocalStorage, "Browser failed write to local storage. " + Util.getExceptionName(e), { exception: Util.dump(e) });
            }
        }
        return false;
    };
    /**
     *  Remove an object from the browser's local storage
     *
     *  @param {string} name - the name of the object to remove from storage
     *  @returns {boolean} True if the storage object could be removed.
     */
    Util.removeStorage = function (logger, name) {
        var storage = Util._getLocalStorageObject();
        if (storage !== null) {
            try {
                storage.removeItem(name);
                return true;
            }
            catch (e) {
                Util._canUseLocalStorage = false;
                logger.throwInternal(LoggingSeverity.WARNING, _InternalMessageId.BrowserFailedRemovalFromLocalStorage, "Browser failed removal of local storage item. " + Util.getExceptionName(e), { exception: Util.dump(e) });
            }
        }
        return false;
    };
    /**
     * Gets the sessionStorage object if available
     * @return {Storage} - Returns the storage object if available else returns null
     */
    Util._getSessionStorageObject = function () {
        if (Util.canUseSessionStorage()) {
            return Util._getVerifiedStorageObject(StorageType.SessionStorage);
        }
        return null;
    };
    /**
     *  Check if the browser supports session storage.
     *
     *  @returns {boolean} True if session storage is supported.
     */
    Util.canUseSessionStorage = function () {
        if (Util._canUseSessionStorage === undefined) {
            Util._canUseSessionStorage = !!Util._getVerifiedStorageObject(StorageType.SessionStorage);
        }
        return Util._canUseSessionStorage;
    };
    /**
     *  Gets the list of session storage keys
     *
     *  @returns {string[]} List of session storage keys
     */
    Util.getSessionStorageKeys = function () {
        var keys = [];
        if (Util.canUseSessionStorage()) {
            objForEachKey(getGlobalInst("sessionStorage"), function (key) {
                keys.push(key);
            });
        }
        return keys;
    };
    /**
     *  Get an object from the browser's session storage
     *
     *  @param {string} name - the name of the object to get from storage
     *  @returns {string} The contents of the storage object with the given name. Null if storage is not supported.
     */
    Util.getSessionStorage = function (logger, name) {
        var storage = Util._getSessionStorageObject();
        if (storage !== null) {
            try {
                return storage.getItem(name);
            }
            catch (e) {
                Util._canUseSessionStorage = false;
                logger.throwInternal(LoggingSeverity.WARNING, _InternalMessageId.BrowserCannotReadSessionStorage, "Browser failed read of session storage. " + Util.getExceptionName(e), { exception: Util.dump(e) });
            }
        }
        return null;
    };
    /**
     *  Set the contents of an object in the browser's session storage
     *
     *  @param {string} name - the name of the object to set in storage
     *  @param {string} data - the contents of the object to set in storage
     *  @returns {boolean} True if the storage object could be written.
     */
    Util.setSessionStorage = function (logger, name, data) {
        var storage = Util._getSessionStorageObject();
        if (storage !== null) {
            try {
                storage.setItem(name, data);
                return true;
            }
            catch (e) {
                Util._canUseSessionStorage = false;
                logger.throwInternal(LoggingSeverity.WARNING, _InternalMessageId.BrowserCannotWriteSessionStorage, "Browser failed write to session storage. " + Util.getExceptionName(e), { exception: Util.dump(e) });
            }
        }
        return false;
    };
    /**
     *  Remove an object from the browser's session storage
     *
     *  @param {string} name - the name of the object to remove from storage
     *  @returns {boolean} True if the storage object could be removed.
     */
    Util.removeSessionStorage = function (logger, name) {
        var storage = Util._getSessionStorageObject();
        if (storage !== null) {
            try {
                storage.removeItem(name);
                return true;
            }
            catch (e) {
                Util._canUseSessionStorage = false;
                logger.throwInternal(LoggingSeverity.WARNING, _InternalMessageId.BrowserFailedRemovalFromSessionStorage, "Browser failed removal of session storage item. " + Util.getExceptionName(e), { exception: Util.dump(e) });
            }
        }
        return false;
    };
    /*
     * Force the SDK not to store and read any data from cookies
     */
    Util.disableCookies = function () {
        CoreUtils.disableCookies();
    };
    /*
     * helper method to tell if document.cookie object is available
     */
    Util.canUseCookies = function (logger) {
        if (CoreUtils._canUseCookies === undefined) {
            CoreUtils._canUseCookies = false;
            try {
                CoreUtils._canUseCookies = Util.document.cookie !== undefined;
            }
            catch (e) {
                logger.throwInternal(LoggingSeverity.WARNING, _InternalMessageId.CannotAccessCookie, "Cannot access document.cookie - " + Util.getExceptionName(e), { exception: Util.dump(e) });
            }
            ;
        }
        return CoreUtils._canUseCookies;
    };
    Util.disallowsSameSiteNone = function (userAgent) {
        if (!_isString(userAgent)) {
            return false;
        }
        // Cover all iOS based browsers here. This includes:
        // - Safari on iOS 12 for iPhone, iPod Touch, iPad
        // - WkWebview on iOS 12 for iPhone, iPod Touch, iPad
        // - Chrome on iOS 12 for iPhone, iPod Touch, iPad
        // All of which are broken by SameSite=None, because they use the iOS networking stack
        if (userAgent.indexOf("CPU iPhone OS 12") !== -1 || userAgent.indexOf("iPad; CPU OS 12") !== -1) {
            return true;
        }
        // Cover Mac OS X based browsers that use the Mac OS networking stack. This includes:
        // - Safari on Mac OS X
        // This does not include:
        // - Internal browser on Mac OS X
        // - Chrome on Mac OS X
        // - Chromium on Mac OS X
        // Because they do not use the Mac OS networking stack.
        if (userAgent.indexOf("Macintosh; Intel Mac OS X 10_14") !== -1 && userAgent.indexOf("Version/") !== -1 && userAgent.indexOf("Safari") !== -1) {
            return true;
        }
        // Cover Mac OS X internal browsers that use the Mac OS networking stack. This includes:
        // - Internal browser on Mac OS X
        // This does not include:
        // - Safari on Mac OS X
        // - Chrome on Mac OS X
        // - Chromium on Mac OS X
        // Because they do not use the Mac OS networking stack.
        if (userAgent.indexOf("Macintosh; Intel Mac OS X 10_14") !== -1 && _endsWith(userAgent, "AppleWebKit/605.1.15 (KHTML, like Gecko)")) {
            return true;
        }
        // Cover Chrome 50-69, because some versions are broken by SameSite=None, and none in this range require it.
        // Note: this covers some pre-Chromium Edge versions, but pre-Chromim Edge does not require SameSite=None, so this is fine.
        // Note: this regex applies to Windows, Mac OS X, and Linux, deliberately.
        if (userAgent.indexOf("Chrome/5") !== -1 || userAgent.indexOf("Chrome/6") !== -1) {
            return true;
        }
        // Unreal Engine runs Chromium 59, but does not advertise as Chrome until 4.23. Treat versions of Unreal
        // that don't specify their Chrome version as lacking support for SameSite=None.
        if (userAgent.indexOf("UnrealEngine") !== -1 && userAgent.indexOf("Chrome") === -1) {
            return true;
        }
        // UCBrowser < 12.13.2 ignores Set-Cookie headers with SameSite=None
        // NB: this rule isn't complete - you need regex to make a complete rule.
        // See: https://www.chromium.org/updates/same-site/incompatible-clients
        if (userAgent.indexOf("UCBrowser/12") !== -1 || userAgent.indexOf("UCBrowser/11") !== -1) {
            return true;
        }
        return false;
    };
    /**
     * helper method to set userId and sessionId cookie
     */
    Util.setCookie = function (logger, name, value, domain) {
        if (Util.canUseCookies(logger)) {
            var domainAttrib = "";
            var secureAttrib = "";
            if (domain) {
                domainAttrib = ";domain=" + domain;
            }
            var location_1 = getLocation();
            if (location_1 && location_1.protocol === "https:") {
                secureAttrib = ";secure";
                if (_uaDisallowsSameSiteNone === null) {
                    _uaDisallowsSameSiteNone = Util.disallowsSameSiteNone((getNavigator() || {}).userAgent);
                }
                if (!_uaDisallowsSameSiteNone) {
                    value = value + ";SameSite=None"; // SameSite can only be set on secure pages
                }
            }
            Util.document.cookie = name + "=" + value + domainAttrib + ";path=/" + secureAttrib;
        }
    };
    Util.stringToBoolOrDefault = function (str, defaultValue) {
        if (defaultValue === void 0) { defaultValue = false; }
        if (str === undefined || str === null) {
            return defaultValue;
        }
        return str.toString().toLowerCase() === "true";
    };
    /**
     * helper method to access userId and sessionId cookie
     */
    Util.getCookie = function (logger, name) {
        if (!Util.canUseCookies(logger)) {
            return;
        }
        var value = "";
        if (name && name.length) {
            var cookieName = name + "=";
            var cookies = Util.document.cookie.split(";");
            for (var i = 0; i < cookies.length; i++) {
                var cookie = cookies[i];
                cookie = Util.trim(cookie);
                if (cookie && cookie.indexOf(cookieName) === 0) {
                    value = cookie.substring(cookieName.length, cookies[i].length);
                    break;
                }
            }
        }
        return value;
    };
    /**
     * Deletes a cookie by setting it's expiration time in the past.
     * @param name - The name of the cookie to delete.
     */
    Util.deleteCookie = function (logger, name) {
        if (Util.canUseCookies(logger)) {
            // Setting the expiration date in the past immediately removes the cookie
            Util.document.cookie = name + "=;path=/;expires=Thu, 01 Jan 1970 00:00:01 GMT;";
        }
    };
    /**
     * generate a random 32bit number (-0x80000000..0x7FFFFFFF).
     */
    Util.random32 = function () {
        return CoreUtils.random32(true);
    };
    /**
     * Gets IE version returning the document emulation mode if we are running on IE, or null otherwise
     */
    Util.getIEVersion = function (userAgentStr) {
        if (userAgentStr === void 0) { userAgentStr = null; }
        var myNav = userAgentStr ? userAgentStr.toLowerCase() : (_navigator ? (_navigator.userAgent || "").toLowerCase() : "");
        if (myNav.indexOf("msie") !== -1) {
            return parseInt(myNav.split("msie")[1]);
        }
        else if (myNav.indexOf("trident/")) {
            var tridentVer = parseInt(myNav.split("trident/")[1]);
            if (tridentVer) {
                return tridentVer + 4;
            }
        }
        return null;
    };
    /**
     * Convert ms to c# time span format
     */
    Util.msToTimeSpan = function (totalms) {
        if (isNaN(totalms) || totalms < 0) {
            totalms = 0;
        }
        totalms = Math.round(totalms);
        var ms = "" + totalms % 1000;
        var sec = "" + Math.floor(totalms / 1000) % 60;
        var min = "" + Math.floor(totalms / (1000 * 60)) % 60;
        var hour = "" + Math.floor(totalms / (1000 * 60 * 60)) % 24;
        var days = Math.floor(totalms / (1000 * 60 * 60 * 24));
        ms = ms.length === 1 ? "00" + ms : ms.length === 2 ? "0" + ms : ms;
        sec = sec.length < 2 ? "0" + sec : sec;
        min = min.length < 2 ? "0" + min : min;
        hour = hour.length < 2 ? "0" + hour : hour;
        return (days > 0 ? days + "." : "") + hour + ":" + min + ":" + sec + "." + ms;
    };
    /**
     * Checks if error has no meaningful data inside. Ususally such errors are received by window.onerror when error
     * happens in a script from other domain (cross origin, CORS).
     */
    Util.isCrossOriginError = function (message, url, lineNumber, columnNumber, error) {
        return (message === "Script error." || message === "Script error") && !error;
    };
    /**
     * Returns string representation of an object suitable for diagnostics logging.
     */
    Util.dump = function (object) {
        var objectTypeDump = Object[strPrototype].toString.call(object);
        var propertyValueDump = "";
        if (objectTypeDump === "[object Error]") {
            propertyValueDump = "{ stack: '" + object.stack + "', message: '" + object.message + "', name: '" + object.name + "'";
        }
        else if (hasJSON()) {
            propertyValueDump = getJSON().stringify(object);
        }
        return objectTypeDump + propertyValueDump;
    };
    /**
     * Returns the name of object if it's an Error. Otherwise, returns empty string.
     */
    Util.getExceptionName = function (object) {
        var objectTypeDump = Object[strPrototype].toString.call(object);
        if (objectTypeDump === "[object Error]") {
            return object.name;
        }
        return "";
    };
    /**
     * Adds an event handler for the specified event to the window
     * @param eventName {string} - The name of the event
     * @param callback {any} - The callback function that needs to be executed for the given event
     * @return {boolean} - true if the handler was successfully added
     */
    Util.addEventHandler = function (eventName, callback) {
        return EventHelper.Attach(getWindow(), eventName, callback);
    };
    /**
     * Tells if a browser supports a Beacon API
     */
    Util.IsBeaconApiSupported = function () {
        return ('sendBeacon' in _navigator && _navigator.sendBeacon);
    };
    Util.getExtension = function (extensions, identifier) {
        var extension = null;
        var extIx = 0;
        while (!extension && extIx < extensions.length) {
            if (extensions[extIx] && extensions[extIx].identifier === identifier) {
                extension = extensions[extIx];
            }
            extIx++;
        }
        return extension;
    };
    Util.document = getDocument() || {};
    Util._canUseLocalStorage = undefined;
    Util._canUseSessionStorage = undefined;
    // listing only non-geo specific locations
    Util._internalEndpoints = [
        "https://dc.services.visualstudio.com/v2/track",
        "https://breeze.aimon.applicationinsights.io/v2/track",
        "https://dc-int.services.visualstudio.com/v2/track"
    ];
    Util.NotSpecified = "not_specified";
    /**
     * helper method to trim strings (IE8 does not implement String.prototype.trim)
     */
    Util.trim = CoreUtils.strTrim;
    /**
     * generate random id string
     */
    Util.newId = CoreUtils.newId;
    /**
     * generate W3C trace id
     */
    Util.generateW3CId = CoreUtils.generateW3CId;
    /**
     * Check if an object is of type Array
     */
    Util.isArray = CoreUtils.isArray;
    /**
     * Check if an object is of type Error
     */
    Util.isError = CoreUtils.isError;
    /**
     * Check if an object is of type Date
     */
    Util.isDate = CoreUtils.isDate;
    // Keeping this name for backward compatibility (for now)
    Util.toISOStringForIE8 = CoreUtils.toISOString;
    return Util;
}());
export { Util };
var UrlHelper = /** @class */ (function () {
    function UrlHelper() {
    }
    UrlHelper.parseUrl = function (url) {
        var anchorIdx = UrlHelper._htmlAnchorIdx;
        var anchorCache = UrlHelper._htmlAnchorElement;
        var tempAnchor = anchorCache[anchorIdx];
        if (!UrlHelper.document.createElement) {
            // Always create the temp instance if createElement is not available
            tempAnchor = { host: UrlHelper.parseHost(url, true) };
        }
        else if (!anchorCache[anchorIdx]) {
            // Create and cache the unattached anchor instance 
            tempAnchor = anchorCache[anchorIdx] = UrlHelper.document.createElement('a');
        }
        tempAnchor.href = url;
        // Move the cache index forward
        anchorIdx++;
        if (anchorIdx >= anchorCache.length) {
            anchorIdx = 0;
        }
        UrlHelper._htmlAnchorIdx = anchorIdx;
        return tempAnchor;
    };
    UrlHelper.getAbsoluteUrl = function (url) {
        var result;
        var a = UrlHelper.parseUrl(url);
        if (a) {
            result = a.href;
        }
        return result;
    };
    UrlHelper.getPathName = function (url) {
        var result;
        var a = UrlHelper.parseUrl(url);
        if (a) {
            result = a.pathname;
        }
        return result;
    };
    UrlHelper.getCompleteUrl = function (method, absoluteUrl) {
        if (method) {
            return method.toUpperCase() + " " + absoluteUrl;
        }
        else {
            return absoluteUrl;
        }
    };
    // Fallback method to grab host from url if document.createElement method is not available
    UrlHelper.parseHost = function (url, inclPort) {
        var fullHost = UrlHelper.parseFullHost(url, inclPort);
        if (fullHost) {
            var match = fullHost.match(/(www[0-9]?\.)?(.[^/:]+)(\:[\d]+)?/i);
            if (match != null && match.length > 3 && _isString(match[2]) && match[2].length > 0) {
                return match[2] + (match[3] || "");
            }
        }
        return fullHost;
    };
    /**
     * Get the full host from the url, optionally including the port
     */
    UrlHelper.parseFullHost = function (url, inclPort) {
        var result = null;
        if (url) {
            var match = url.match(/(\w*):\/\/(.[^/:]+)(\:[\d]+)?/i);
            if (match != null && match.length > 2 && _isString(match[2]) && match[2].length > 0) {
                result = match[2] || "";
                if (inclPort && match.length > 2) {
                    var protocol = (match[1] || "").toLowerCase();
                    var port = match[3] || "";
                    // IE includes the standard port so pass it off if it's the same as the protocol
                    if (protocol === "http" && port === ":80") {
                        port = "";
                    }
                    else if (protocol === "https" && port === ":443") {
                        port = "";
                    }
                    result += port;
                }
            }
        }
        return result;
    };
    UrlHelper.document = getDocument() || {};
    UrlHelper._htmlAnchorIdx = 0;
    // Use an array of temporary values as it's possible for multiple calls to parseUrl() will be called with different URLs
    // Using a cache size of 5 for now as it current depth usage is at least 2, so adding a minor buffer to handle future updates
    UrlHelper._htmlAnchorElement = [null, null, null, null, null];
    return UrlHelper;
}());
export { UrlHelper };
var CorrelationIdHelper = /** @class */ (function () {
    function CorrelationIdHelper() {
    }
    /**
     * Checks if a request url is not on a excluded domain list and if it is safe to add correlation headers.
     * Headers are always included if the current domain matches the request domain. If they do not match (CORS),
     * they are regex-ed across correlationHeaderDomains and correlationHeaderExcludedDomains to determine if headers are included.
     * Some environments don't give information on currentHost via window.location.host (e.g. Cordova). In these cases, the user must
     * manually supply domains to include correlation headers on. Else, no headers will be included at all.
     */
    CorrelationIdHelper.canIncludeCorrelationHeader = function (config, requestUrl, currentHost) {
        if (!requestUrl || (config && config.disableCorrelationHeaders)) {
            return false;
        }
        if (config && config.correlationHeaderExcludePatterns) {
            for (var i = 0; i < config.correlationHeaderExcludePatterns.length; i++) {
                if (config.correlationHeaderExcludePatterns[i].test(requestUrl)) {
                    return false;
                }
            }
        }
        var requestHost = UrlHelper.parseUrl(requestUrl).host.toLowerCase();
        if (requestHost && (requestHost.indexOf(":443") !== -1 || requestHost.indexOf(":80") !== -1)) {
            // [Bug #1260] IE can include the port even for http and https URLs so if present 
            // try and parse it to remove if it matches the default protocol port
            requestHost = (UrlHelper.parseFullHost(requestUrl, true) || "").toLowerCase();
        }
        if ((!config || !config.enableCorsCorrelation) && requestHost !== currentHost) {
            return false;
        }
        var includedDomains = config && config.correlationHeaderDomains;
        if (includedDomains) {
            var matchExists_1;
            CoreUtils.arrForEach(includedDomains, function (domain) {
                var regex = new RegExp(domain.toLowerCase().replace(/\./g, "\.").replace(/\*/g, ".*"));
                matchExists_1 = matchExists_1 || regex.test(requestHost);
            });
            if (!matchExists_1) {
                return false;
            }
        }
        var excludedDomains = config && config.correlationHeaderExcludedDomains;
        if (!excludedDomains || excludedDomains.length === 0) {
            return true;
        }
        for (var i = 0; i < excludedDomains.length; i++) {
            var regex = new RegExp(excludedDomains[i].toLowerCase().replace(/\./g, "\.").replace(/\*/g, ".*"));
            if (regex.test(requestHost)) {
                return false;
            }
        }
        // if we don't know anything about the requestHost, require the user to use included/excludedDomains.
        // Previously we always returned false for a falsy requestHost
        return requestHost && requestHost.length > 0;
    };
    /**
     * Combines target appId and target role name from response header.
     */
    CorrelationIdHelper.getCorrelationContext = function (responseHeader) {
        if (responseHeader) {
            var correlationId = CorrelationIdHelper.getCorrelationContextValue(responseHeader, RequestHeaders.requestContextTargetKey);
            if (correlationId && correlationId !== CorrelationIdHelper.correlationIdPrefix) {
                return correlationId;
            }
        }
    };
    /**
     * Gets key from correlation response header
     */
    CorrelationIdHelper.getCorrelationContextValue = function (responseHeader, key) {
        if (responseHeader) {
            var keyValues = responseHeader.split(",");
            for (var i = 0; i < keyValues.length; ++i) {
                var keyValue = keyValues[i].split("=");
                if (keyValue.length === 2 && keyValue[0] === key) {
                    return keyValue[1];
                }
            }
        }
    };
    CorrelationIdHelper.correlationIdPrefix = "cid-v1:";
    return CorrelationIdHelper;
}());
export { CorrelationIdHelper };
var AjaxHelper = /** @class */ (function () {
    function AjaxHelper() {
    }
    AjaxHelper.ParseDependencyPath = function (logger, absoluteUrl, method, commandName) {
        var target, name = commandName, data = commandName;
        if (absoluteUrl && absoluteUrl.length > 0) {
            var parsedUrl = UrlHelper.parseUrl(absoluteUrl);
            target = parsedUrl.host;
            if (!name) {
                if (parsedUrl.pathname != null) {
                    var pathName = (parsedUrl.pathname.length === 0) ? "/" : parsedUrl.pathname;
                    if (pathName.charAt(0) !== '/') {
                        pathName = "/" + pathName;
                    }
                    data = parsedUrl.pathname;
                    name = DataSanitizer.sanitizeString(logger, method ? method + " " + pathName : pathName);
                }
                else {
                    name = DataSanitizer.sanitizeString(logger, absoluteUrl);
                }
            }
        }
        else {
            target = commandName;
            name = commandName;
        }
        return {
            target: target,
            name: name,
            data: data
        };
    };
    return AjaxHelper;
}());
export { AjaxHelper };
/**
 * A utility class that helps getting time related parameters
 */
var DateTimeUtils = /** @class */ (function () {
    function DateTimeUtils() {
    }
    /**
     * Get the number of milliseconds since 1970/01/01 in local timezone
     */
    DateTimeUtils.Now = function () {
        // returns the window or webworker performance object
        var perf = getPerformance();
        if (perf && perf.now && perf.timing) {
            var now = perf.now() + perf.timing.navigationStart;
            // Known issue with IE where this calculation can be negative, so if it is then ignore and fallback
            if (now > 0) {
                return now;
            }
        }
        return new Date().getTime();
    };
    /**
     * Gets duration between two timestamps
     */
    DateTimeUtils.GetDuration = function (start, end) {
        var result = null;
        if (start !== 0 && end !== 0 && !CoreUtils.isNullOrUndefined(start) && !CoreUtils.isNullOrUndefined(end)) {
            result = end - start;
        }
        return result;
    };
    return DateTimeUtils;
}());
export { DateTimeUtils };
//# sourceMappingURL=Util.js.map